import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import "./styles/mobile.css";
import App from "@/App";

// Babylon.js polygon extrusion (ExtrudePolygon) requires `earcut` for
// triangulation. The library reads it from `window.earcut` at runtime,
// so we expose it here. NOTE: this is a hard requirement for the
// MasterPlan3DView (Babylon.js viewer). If you remove the 3D view,
// you can drop these two lines.
import earcut from "earcut";
window.earcut = earcut;

// React Flow's internal layout engine triggers a benign "ResizeObserver
// loop completed with undelivered notifications" warning. CRA's dev-
// server overlay elevates it to a fullscreen modal that blocks pointer
// events — catastrophic for the Overview Fishbone interactivity.
//
// The reliable fix is to wrap ResizeObserver callbacks in
// requestAnimationFrame so the browser's loop-guard never trips. We also
// keep layer 2/3 suppression (window error + console.error filter +
// MutationObserver iframe sweep) as a safety net.
if (typeof window !== "undefined") {
  const SILENCED = "ResizeObserver loop";
  const isSilenced = (m) => typeof m === "string" && m.includes(SILENCED);

  // Layer 1 — patch ResizeObserver at the source.
  if (typeof window.ResizeObserver === "function") {
    const NativeRO = window.ResizeObserver;
    window.ResizeObserver = class PatchedRO extends NativeRO {
      constructor(cb) {
        super((entries, observer) => {
          window.requestAnimationFrame(() => {
            try { cb(entries, observer); } catch (err) {
              if (!isSilenced(err?.message)) throw err;
            }
          });
        });
      }
    };
  }

  // Layer 2 — swallow the window error events (capture phase, before the
  // CRA dev overlay listener can see them).
  window.addEventListener("error", (e) => {
    if (isSilenced(e?.message)) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return false;
    }
  }, true);
  window.addEventListener("unhandledrejection", (e) => {
    if (isSilenced(e?.reason?.message)) e.preventDefault();
  }, true);

  // Layer 3 — filter console.error so source-map lookups don't trigger
  // the overlay via other code paths.
  const origError = console.error.bind(console);
  console.error = (...args) => {
    if (args.some((a) => isSilenced(typeof a === "string" ? a : a?.message))) return;
    origError(...args);
  };

  // Layer 4 — safety net: if the overlay still slips through, remove it.
  const dismissOverlay = () => {
    document.querySelectorAll(
      'iframe[id^="webpack-dev-server-client-overlay"], iframe#webpack-dev-server-client-overlay, iframe[src*="webpack-dev-server"]'
    ).forEach((f) => { try { f.remove(); } catch {} });
    document.querySelectorAll('div[id^="webpack-dev-server-client-overlay"]').forEach((d) => {
      try { d.remove(); } catch {}
    });
  };
  const mo = new MutationObserver(() => dismissOverlay());
  const start = () => mo.observe(document.body, { childList: true, subtree: false });
  if (document.body) start();
  else document.addEventListener("DOMContentLoaded", start, { once: true });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
